import _ from "lodash";
import { APP_RESULT_SHOW_3_RESULTS } from "consts";
import { subValList } from "utils";
import { findLuminaireFamily, roundToTwo } from "utils/helpers";
import type { ILightingClassConfig } from "types";
import type { IResultItem, RawResultEntry, FamilyApiResult, SelectResultApiData, SelectResultResponse, FamiliesResponse, FamilyItem } from "../types";

export const mapResultItem = (
  entry: RawResultEntry,
  families: FamiliesResponse,
  placingStyle: string,
  maintenanceFactor: number,
  lightingClassConfig: ILightingClassConfig,
  familyIndex?: number,
  indexInResult?: number
): IResultItem => {
  const nbr_luminaires = entry.n_lums;
  const power = entry.config.power;
  const delta = entry.lum_row.delta;
  let num_rows = 1;
  if (placingStyle === "Both" || placingStyle === "Staggered" || placingStyle === "Centered") {
    num_rows = 2;
  }
  const loadperkm = (1000 / delta) * num_rows * power;
  const lph = entry.lum_row.lph;
  const name = entry.config.ldc_id;
  const lampset = entry.config.lampset_name;
  const result = entry.result;

  const articleId = entry.config.article_id ?? entry.article_id ?? "";

  const { familyIndex: correctFamilyIndex, familyId, imageUrl } = findLuminaireFamily(articleId, name, families, familyIndex);

  const f_ti = entry.result.f_ti;
  const ulor = entry.result.ulor;

  let s_state: string | null = null;
  if (/^[P|C][0-9]/.test(lightingClassConfig.roadClasses)) {
    s_state = entry.result.e_state.toLowerCase();
  }
  if (/^[M][0-9]/.test(lightingClassConfig.roadClasses)) {
    s_state = (entry.result.l_state ?? "").toLowerCase();
  }

  const subValListOfSidewalks = subValList(entry.result.subVal);
  let e_state_left = "";
  let e_state_right = "";
  if (subValListOfSidewalks) {
    const indexOfLeft = subValListOfSidewalks.indexOf("sd-fpath-left");
    const indexOfRight = subValListOfSidewalks.indexOf("sd-fpath-right");
    e_state_left = indexOfLeft > -1 ? (entry.result.subVal?.[indexOfLeft].e_state.toLowerCase() ?? "") : "";
    e_state_right = indexOfRight > -1 ? (entry.result.subVal?.[indexOfRight].e_state.toLowerCase() ?? "") : "";
  }

  return {
    name,
    article_id: articleId,
    family_id: familyId,
    family_image: imageUrl,
    lampset,
    e_lvk_png_url: entry.config.lvk_png_url,  // @deprecated
    e_lvk_img_url: entry.config.lvk_img_url,
    LuminousFlux: entry.config.light_flux,
    PowerConsumption: power,
    Load: roundToTwo(loadperkm),
    DistancePoles: delta,
    Overhang: entry.lum_row.overhang,
    Height: lph,
    Tilt: entry.lum_row.tilt,
    Maintenance: maintenanceFactor,
    RoadStandard: s_state,
    borderAreaLeftStandard: e_state_left,
    borderAreaRightStandard: e_state_right,
    f_ti,
    ulor,
    result,
    familyIndex: correctFamilyIndex,
    indexInResult,
    nbr_luminaires,
  };
};

export const mapResults = (
  response: SelectResultResponse,
  families: FamiliesResponse,
  placingStyle: string,
  maintenanceFactor: number,
  lightingClassConfig: ILightingClassConfig
): IResultItem[] => {
  const show3ResultsOnly = APP_RESULT_SHOW_3_RESULTS;
  const rawData = typeof response.data === "string" ? JSON.parse(response.data.replaceAll("Infinity", "0")) : response.data;
  const cleanData = rawData as SelectResultApiData;

  const mapped = cleanData.result.map((familyResult: FamilyApiResult) => {
    const family_ids = familyResult.request_json.article_ids;
    const familyIndex = _.findIndex(families.data.families, (family: FamilyItem) => family_ids?.includes(family.id));
    const innResult = familyResult.result?.result;
    const sliceSize = show3ResultsOnly && innResult ? 3 : innResult?.length;
    return innResult
      ? innResult
          .slice(0, sliceSize)
          .map((e: RawResultEntry, index: number) => mapResultItem(e, families, placingStyle, maintenanceFactor, lightingClassConfig, familyIndex, index))
      : null;
  });

  const flat = mapped.flat();
  return flat[0] === null ? [] : (flat as IResultItem[]);
};
